import { APP_TYPE, AppType } from "../app.type";
import { Docs } from "../../../../shared/components";
import {
  doc2Model,
  getProfile,
  getProfiles,
  model2Doc,
  updateProfile,
  updateProfileFavorite,
} from "../api.service";
import { Inject, Injectable } from "@angular/core";
import { Profile } from "../../../../shared/models/profile";
import {
  ProfileField,
  ProfileServiceType,
  ProfileWithSections,
} from "./profile.service.type";
import { BehaviorSubject } from "rxjs";
import { loadModel } from "../utils/app.utils";
import { Customer } from "../../../../shared/types/customers";
import { EncodingUtils } from "../../../../shared/utils/encoding.utils";
import { StringUtils } from "../../../../shared/utils/string.utils";

@Injectable()
export class ProfileService implements ProfileServiceType {
  profiles: Profile[] = [];
  ids: string[] = [];
  profile: Profile = {} as Profile;
  ownProfile: Profile = {} as Profile;
  profileWithSections: ProfileWithSections = {} as ProfileWithSections;
  cleanProfileWithSections: ProfileWithSections = {} as ProfileWithSections;
  ownProfilePicture: string | null = "";
  userProfilePicture: string | null = "";
  ownProfilePictureChanges: BehaviorSubject<string> = new BehaviorSubject("");
  viewProfile: boolean = false;
  selectedProfile: string = "";
  profileNotFound = false;

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  get user() {
    return this.app.state.profileUser;
  }

  async getProfiles() {
    this.profiles = await getProfiles(this.app.customers.expectCurrent);
    this.ids = this.profiles.map((p) => p.id);

    return this.profiles;
  }

  async initProfile(id: string) {
    this.app.profile.viewProfile = true;
    if (this.app.taskPanel.isOpen) {
      this.app.taskPanel.isOpen = false;
    }

    if (StringUtils.isUndefined(id)) {
      this.profileNotFound = true;
      this.app.state.profileUser = "";
      this.profile = {} as Profile;
    }
    let result = await this.getProfile(id);
    this.app.profile.viewProfile = true;
    if (result != null) {
      this.profileNotFound = false;
      this.app.profile.loadProfileModelWithSections();
      this.app.state.profileUser = this.profile.name as any;
      this.app.profile.selectedProfile = this.profile.name as any;
      this.app.profile.userProfilePicture =
        await this.app.profile.getProfilePicture(this.profile.id);
      this.app.spinner.hideSpinner();
    } else {
      this.profileNotFound = true;
      this.app.state.profileUser = "";
      this.app.profile.selectedProfile = "";
      this.profile = {} as Profile;
      this.app.profile.loadProfileModelWithSections();
      this.app.spinner.hideSpinner();
    }
  }

  async getProfile(id: string): Promise<Profile> {
    if (id == null) {
      return {} as Profile;
    }
    try {
      this.profile = await getProfile(id);
      this.ownProfile = this.profile;
    } catch (err) {
      this.profile = {} as Profile;
      this.profileNotFound = true;
    }
    return this.profile;
  }

  loadProfileModelWithSections() {
    this.profileWithSections = doc2Model(
      "profile",
      this.profile
    ) as ProfileWithSections;
    loadModel("profile", this.profile, this.profileWithSections);

    if (this.profile.sections) {
      loadModel(
        "personProfile",
        this.profile.sections.pers,
        this.profileWithSections
      );
      loadModel(
        "organizationProfile",
        this.profile.sections.org,
        this.profileWithSections
      );
    }
    Object.assign(this.cleanProfileWithSections, this.profileWithSections);
  }

  async getProfilePicture(id: string | null) {
    if (id == null) {
      return "";
    }

    let fileLink = "";
    const customer = this.app.customers.expectCurrent;
    const ownProfile = this.app.state.userId === id ? true : false;
    const attachments = await this.app.file.getAttachmentsByDocType(
      id,
      "profile"
    );

    // If no files were attached to the profile, an object which has the length property 0 will be returned
    if (Object.keys(attachments).length === 0) {
      if (ownProfile) {
        this.ownProfilePicture = null;
      }
    } else {
      const fileName = Object.keys(attachments)[0];
      fileLink = [customer, "profile", id, fileName].join("/");
      if (!fileName.includes("user.jpg")) {
        fileLink = "";
      }
      if (ownProfile) {
        this.ownProfilePicture = fileLink;
      }
    }
    return fileLink;
  }

  async updateProfile() {
    const personProfile = model2Doc("personProfile", this.profileWithSections);
    const organizationProfile = model2Doc(
      "organizationProfile",
      this.profileWithSections
    );

    // Encode the profile sections in a JSON string to be saved as the 'data'
    this.profile.data = this.getProfileDataJson(
      personProfile,
      organizationProfile
    );
    delete this.profile.sections;

    var result = await updateProfile(this.profile);
    if (result.ok) {
      this.app.state.next({
        hasSuccess: true,
        successText: this.app.text.profile.successMessage,
      });
      setTimeout(() => {
        this.app.state.next({ hasSuccess: false, successText: "" });
      }, 3000);
    }
    await this.getProfile(this.profile.id);
    this.loadProfileModelWithSections();

    if (this.app.customers.expectCurrent === Customer.OC) {
      this.app.OCHome.setBadgeSystem();
    }
    this.app.field.inputSubject.next(true);
  }

  async updateMultipleFavorite(
    profileIds: string[],
    customer = this.app.customers.expectCurrent,
    threadId: string,
    toggle: boolean
  ): Promise<void> {
    await updateProfileFavorite(profileIds, customer, threadId, toggle);
  }

  async uploadProfilePicture(file: File) {
    const { customer } = this.app;

    if (customer == null || this.profile.id == null) {
      return;
    }

    if (file != null && file.type.indexOf("image/") !== 0) {
      this.app.hasError = true;
      this.app.errorText = this.app.text.thread.uploadImageTypeError;
      return;
    }

    if (this.app.file.isFileTooLarge(file, true)) {
      this.app.hasError = true;
      this.app.errorText = this.app.text.app.profileFileTooLargeError;
      return;
    }

    this.app.hasError = false;
    this.app.errorText = "";

    let fileLink = [
      customer,
      "profile",
      EncodingUtils.encodeBase64(this.profile.id),
      "user.jpg",
    ].join("/");
    const result = await this.app.file.upload(file, fileLink);

    fileLink = [customer, "profile", this.profile.id, "user.jpg"].join("/");
    this.ownProfilePictureChanges.next(fileLink);
    this.userProfilePicture = fileLink;
    this.app.profile.userProfilePicture =
      await this.app.profile.getProfilePicture(this.profile.id);
  }

  async deleteProfilePicture() {
    const { customer, user } = this.app;

    if (customer == null || user == null) {
      return;
    }

    /** !!! DELETE any attachment that has been added to the profiles independent on the name !!!  */
    const fileLink = [
      customer,
      "profile",
      EncodingUtils.encodeBase64(this.profile.id),
      "user.jpg",
    ].join("/");
    await this.app.file.delete(fileLink);

    this.ownProfilePictureChanges.next("");
    this.userProfilePicture = "";
  }

  private getProfileDataJson(
    pers: Docs["personProfile"],
    org: Docs["organizationProfile"]
  ): string {
    const persFieldList: ProfileField[] = [];
    const orgFieldList: ProfileField[] = [];

    Object.keys(pers)
      .filter((key) => key !== "publicFields")
      .forEach((key) => {
        persFieldList.push({
          title: key,
          value: (pers as any)[key],
        });
      });

    Object.keys(org)
      .filter((key) => key !== "publicFields")
      .forEach((key) => {
        orgFieldList.push({
          title: key,
          value: (org as any)[key],
        });
      });

    const profileData = {
      pers: persFieldList,
      org: orgFieldList,
    };

    return JSON.stringify(profileData);
  }

  canEdit2FAForUser() {
    const customer = this.app.customers.expectCurrent;
    switch (customer) {
      case Customer.MTU:
      case Customer.SEW:
      case Customer.VIASTORE:
        return true;
      default:
        return false;
    }
  }
}
